import { useState, useEffect } from "react";
import { api } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import {
  MagnifyingGlass,
  Plus,
  Trash,
  PencilSimple,
  User,
  EnvelopeSimple,
  Phone,
  Files,
} from "@phosphor-icons/react";
import { format } from "date-fns";

const ClientsPage = () => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [showDialog, setShowDialog] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientProposals, setClientProposals] = useState([]);

  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    nationality: "",
    notes: "",
  });

  useEffect(() => {
    fetchClients();
  }, [search]);

  const fetchClients = async () => {
    try {
      const params = search ? `?search=${encodeURIComponent(search)}` : "";
      const res = await api.get(`/clients${params}`);
      setClients(res.data);
    } catch (error) {
      toast.error("Failed to fetch clients");
    } finally {
      setLoading(false);
    }
  };

  const fetchClientProposals = async (clientId) => {
    try {
      const res = await api.get(`/clients/${clientId}/proposals`);
      setClientProposals(res.data);
    } catch (error) {
      console.error("Failed to fetch client proposals:", error);
    }
  };

  const handleOpenDialog = (client = null) => {
    if (client) {
      setEditingClient(client);
      setForm({
        name: client.name,
        email: client.email,
        phone: client.phone,
        nationality: client.nationality,
        notes: client.notes,
      });
    } else {
      setEditingClient(null);
      setForm({ name: "", email: "", phone: "", nationality: "", notes: "" });
    }
    setShowDialog(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.error("Please enter client name");
      return;
    }

    try {
      if (editingClient) {
        await api.put(`/clients/${editingClient.id}`, form);
        toast.success("Client updated");
      } else {
        await api.post("/clients", form);
        toast.success("Client created");
      }
      setShowDialog(false);
      fetchClients();
    } catch (error) {
      toast.error("Failed to save client");
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await api.delete(`/clients/${deleteId}`);
      toast.success("Client deleted");
      setDeleteId(null);
      fetchClients();
    } catch (error) {
      toast.error("Failed to delete client");
    }
  };

  const handleSelectClient = (client) => {
    setSelectedClient(client);
    fetchClientProposals(client.id);
  };

  return (
    <div className="p-8 max-w-6xl" data-testid="clients-page">
      {/* Header */}
      <div className="flex items-start justify-between mb-8">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-gold mb-2">Clients</p>
          <h1 className="font-serif text-4xl text-navy">Client Profiles</h1>
        </div>
        <Button
          onClick={() => handleOpenDialog()}
          className="bg-navy hover:bg-[#2a3a5e] text-white gap-2"
          data-testid="add-client-btn"
        >
          <Plus size={18} />
          Add Client
        </Button>
      </div>

      {/* Search */}
      <div className="relative max-w-md mb-6">
        <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input
          placeholder="Search by name or email..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10 bg-white border-border"
          data-testid="search-clients"
        />
      </div>

      <div className="flex gap-6">
        {/* Clients List */}
        <div className="flex-1 bg-white border border-border">
          {loading ? (
            <div className="p-12 text-center">
              <div className="w-8 h-8 border-2 border-navy border-t-transparent animate-spin mx-auto"></div>
            </div>
          ) : clients.length > 0 ? (
            <div className="divide-y divide-border">
              {clients.map((client) => (
                <div
                  key={client.id}
                  className={`p-4 flex items-center justify-between hover:bg-cream/50 cursor-pointer transition-colors ${
                    selectedClient?.id === client.id ? "bg-cream" : ""
                  }`}
                  onClick={() => handleSelectClient(client)}
                  data-testid={`client-row-${client.id}`}
                >
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 bg-navy/5 flex items-center justify-center">
                      <User size={20} className="text-navy" />
                    </div>
                    <div>
                      <p className="font-medium text-navy">{client.name}</p>
                      {client.email && (
                        <p className="text-xs text-muted-foreground">{client.email}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <p className="text-sm text-muted-foreground">{client.proposal_count} proposals</p>
                      {client.last_proposal_date && (
                        <p className="text-xs text-muted-foreground">
                          Last: {format(new Date(client.last_proposal_date), "MMM d, yyyy")}
                        </p>
                      )}
                    </div>
                    <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleOpenDialog(client)}
                        className="h-8 w-8 p-0"
                      >
                        <PencilSimple size={16} />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setDeleteId(client.id)}
                        className="h-8 w-8 p-0 text-destructive hover:text-destructive hover:bg-destructive/10"
                      >
                        <Trash size={16} />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="p-12 text-center">
              <User size={48} className="mx-auto text-muted-foreground/30 mb-4" />
              <p className="text-muted-foreground mb-4">No clients found</p>
              <Button
                onClick={() => handleOpenDialog()}
                className="bg-gold hover:bg-gold/90 text-white"
              >
                Add your first client
              </Button>
            </div>
          )}
        </div>

        {/* Client Detail */}
        {selectedClient && (
          <div className="w-[320px] bg-white border border-border p-6">
            <div className="flex items-center gap-3 mb-6 pb-4 border-b border-border">
              <div className="w-12 h-12 bg-navy/5 flex items-center justify-center">
                <User size={24} className="text-navy" />
              </div>
              <div>
                <h3 className="font-serif text-lg text-navy">{selectedClient.name}</h3>
                {selectedClient.nationality && (
                  <p className="text-xs text-muted-foreground">{selectedClient.nationality}</p>
                )}
              </div>
            </div>

            {selectedClient.email && (
              <div className="flex items-center gap-3 mb-3">
                <EnvelopeSimple size={16} className="text-muted-foreground" />
                <span className="text-sm">{selectedClient.email}</span>
              </div>
            )}
            {selectedClient.phone && (
              <div className="flex items-center gap-3 mb-3">
                <Phone size={16} className="text-muted-foreground" />
                <span className="text-sm">{selectedClient.phone}</span>
              </div>
            )}
            {selectedClient.notes && (
              <div className="mt-4 pt-4 border-t border-border">
                <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Notes</p>
                <p className="text-sm text-muted-foreground">{selectedClient.notes}</p>
              </div>
            )}

            {/* Client Proposals */}
            <div className="mt-6 pt-4 border-t border-border">
              <p className="text-xs uppercase tracking-wider text-muted-foreground mb-3 flex items-center gap-2">
                <Files size={14} />
                Proposals ({clientProposals.length})
              </p>
              {clientProposals.length > 0 ? (
                <div className="space-y-2">
                  {clientProposals.slice(0, 5).map((p) => (
                    <div key={p.id} className="text-sm p-2 bg-cream">
                      <p className="font-medium text-navy">{p.trip_title || "Untitled"}</p>
                      <p className="text-xs text-muted-foreground">{p.reference}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">No proposals yet</p>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Add/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-serif text-xl text-navy">
              {editingClient ? "Edit Client" : "Add Client"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Full Name *</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Mr & Mrs Sharma"
                className="bg-cream border-border"
                data-testid="client-name-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Email</Label>
                <Input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="client@email.com"
                  className="bg-cream border-border"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Phone</Label>
                <Input
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  placeholder="+91 XXXXX XXXXX"
                  className="bg-cream border-border"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Nationality</Label>
              <Input
                value={form.nationality}
                onChange={(e) => setForm({ ...form, nationality: e.target.value })}
                placeholder="Indian"
                className="bg-cream border-border"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Notes</Label>
              <Textarea
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder="Additional notes about the client..."
                className="bg-cream border-border"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} className="bg-navy hover:bg-[#2a3a5e] text-white" data-testid="save-client-btn">
              {editingClient ? "Update" : "Add Client"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Client</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this client? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ClientsPage;
